import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormLabel,
  Divider,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  alpha,
  Popover,
  InputAdornment,
  Card,
  CardContent,
  IconButton,
  Breadcrumbs,
  Link,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoIcon from "@mui/icons-material/Info";
import CloudIcon from "@mui/icons-material/Cloud";
import PublicIcon from "@mui/icons-material/Public";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import SearchIcon from "@mui/icons-material/Search";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import {
  getCustomerMetadata,
  clearBillingManagementMessages,
} from "../../store/billingManagement/billingManagementSlice";
import { getCustomerMetadata as getCustomerMetadataApi } from "../../helpers/auth_aws_helper";

const CreateBillingRule = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { customerOrgCode } = useParams();

  // Redux state
  const {
    metadata,
    isFetchingMetadata,
    fetchMetadataError,
  } = useSelector((state) => state.billingManagement);

  // Storage key for persisting rule blocks
  const STORAGE_KEY = `billing_rule_blocks_${customerOrgCode}`;

  // Load initial state from sessionStorage or use default
  const getInitialRuleBlocks = () => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Validate that it's an array and has at least one block
        if (Array.isArray(parsed) && parsed.length > 0) {
          // Migrate old state: convert linked_accounts to selectedAccounts/allAccounts
          return parsed.map((block) => {
            let migratedBlock = { ...block };
            
            // If old state has linked_accounts, migrate it
            if (block.linked_accounts !== undefined) {
              const allAccounts = block.linked_accounts === "ALL";
              const selectedAccounts = allAccounts ? [] : (block.linked_accounts ? [block.linked_accounts] : []);
              migratedBlock = {
                ...migratedBlock,
                allAccounts,
                selectedAccounts,
                linked_accounts: undefined, // Remove old field
              };
            }
            
            // Ensure selectedAccounts exists and is an array
            // Default allAccounts to true if undefined (for backward compatibility and default behavior)
            const hasSelectedAccounts = Array.isArray(migratedBlock.selectedAccounts) && migratedBlock.selectedAccounts.length > 0;
            // If allAccounts is undefined, default to true if no accounts are selected
            const defaultAllAccounts = migratedBlock.allAccounts !== undefined ? migratedBlock.allAccounts : !hasSelectedAccounts;
            migratedBlock = {
              ...migratedBlock,
              allAccounts: defaultAllAccounts,
              selectedAccounts: Array.isArray(migratedBlock.selectedAccounts) ? migratedBlock.selectedAccounts : [],
            };
            
            // Fix frequency: non-CUSTOM categories must be RECURRING
            if (migratedBlock.selectedCategory && migratedBlock.selectedCategory !== "CUSTOM" && migratedBlock.frequency === "ONE_TIME") {
              migratedBlock.frequency = "RECURRING";
              migratedBlock.start_date = migratedBlock.start_date || "";
              migratedBlock.end_date = migratedBlock.end_date || "";
            }
            
            return migratedBlock;
          });
        }
      }
    } catch (error) {
      console.error("Error loading saved rule blocks:", error);
    }
    // Default state
    return [
      {
        id: Date.now(),
        name: "",
        description: "",
        rule_category: "",
        rule_type: "",
        frequency: "RECURRING",
        start_date: "",
        end_date: "",
        cost_measurement: "UNBLENDED",
        status: "ACTIVE",
        parameters: {},
        selectedCategory: null,
        selectedType: null,
        selectedTypes: [],
        selectedAccounts: [],
        selectedServices: [],
        selectedRegions: [],
        scopeOperatorAccounts: "INCLUDE",
        scopeOperatorServices: "INCLUDE",
        scopeOperatorRegions: "INCLUDE",
      },
    ];
  };

  // Rule blocks state - array of rule configurations
  const [ruleBlocks, setRuleBlocks] = useState(getInitialRuleBlocks);

  // Save rule blocks to sessionStorage whenever they change
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(ruleBlocks));
    } catch (error) {
      console.error("Error saving rule blocks to sessionStorage:", error);
    }
  }, [ruleBlocks, STORAGE_KEY]);

  // Service and Region metadata
  const [serviceCodes, setServiceCodes] = useState([]);
  const [regions, setRegions] = useState([]);
  const [isFetchingServiceCodes, setIsFetchingServiceCodes] = useState(false);
  const [isFetchingRegions, setIsFetchingRegions] = useState(false);

  // Popover states for each rule block
  const [popoverStates, setPopoverStates] = useState({});

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Fetch metadata when component mounts
  useEffect(() => {
    if (customerOrgCode) {
      dispatch(
        getCustomerMetadata({
          customerOrgCode,
          params: {
            kind: "rule_definitions",
          },
        })
      );
      // Fetch service codes
      setIsFetchingServiceCodes(true);
      getCustomerMetadataApi(customerOrgCode, { kind: "service_codes", size: 200 })
        .then((response) => {
          const { status, ...responseData } = response;
          if (responseData.values) {
            setServiceCodes(responseData.values);
          }
          setIsFetchingServiceCodes(false);
        })
        .catch((error) => {
          console.error("Error fetching service codes:", error);
          setIsFetchingServiceCodes(false);
        });
      // Fetch regions
      setIsFetchingRegions(true);
      getCustomerMetadataApi(customerOrgCode, { kind: "regions" })
        .then((response) => {
          const { status, ...responseData } = response;
          if (responseData.values) {
            setRegions(responseData.values);
          }
          setIsFetchingRegions(false);
        })
        .catch((error) => {
          console.error("Error fetching regions:", error);
          setIsFetchingRegions(false);
        });
    }
  }, [customerOrgCode, dispatch]);

  // Add new rule block
  const handleAddRuleBlock = () => {
    setRuleBlocks([
      ...ruleBlocks,
      {
        id: Date.now(),
        name: "",
        description: "",
        rule_category: "",
        rule_type: "",
        frequency: "RECURRING",
        start_date: "",
        end_date: "",
        cost_measurement: "UNBLENDED",
        status: "ACTIVE",
        parameters: {},
        selectedCategory: null,
        selectedType: null,
        selectedTypes: [],
        allAccounts: true,
        selectedAccounts: [],
        selectedServices: [],
        selectedRegions: [],
        scopeOperatorAccounts: "INCLUDE",
        scopeOperatorServices: "INCLUDE",
        scopeOperatorRegions: "INCLUDE",
      },
    ]);
  };

  // Remove rule block
  const handleRemoveRuleBlock = (blockId) => {
    if (ruleBlocks.length > 1) {
      setRuleBlocks(ruleBlocks.filter((block) => block.id !== blockId));
    }
  };

  // Update rule block field
  const updateRuleBlock = (blockId, field, value) => {
    setRuleBlocks(
      ruleBlocks.map((block) =>
        block.id === blockId ? { ...block, [field]: value } : block
      )
    );
  };

  // Update rule block array field (for selectedServices, selectedRegions)
  const updateRuleBlockArray = (blockId, field, newArray) => {
    setRuleBlocks(
      ruleBlocks.map((block) =>
        block.id === blockId ? { ...block, [field]: newArray } : block
      )
    );
  };

  // Get available types for a category
  const getAvailableTypes = (category) => {
    if (!category) return [];
    return metadata?.categories?.find((cat) => cat.category === category)?.types || [];
  };

  // Get allowed scopes for a category
  const getAllowedScopes = (category) => {
    if (!category) return [];
    const categoryData = metadata?.categories?.find((cat) => cat.category === category);
    return categoryData?.allowed_scopes || [];
  };

  // Check if category only has boolean types
  const isBooleanOnlyCategory = (category) => {
    const types = getAvailableTypes(category);
    if (types.length === 0) return false;
    return types.every((type) => {
      const params = type.required_parameters || [];
      return params.length === 1 && params[0].type === "boolean";
    });
  };

  // Check if category has only one type
  const hasSingleType = (category) => {
    return getAvailableTypes(category).length === 1;
  };

  // Get required parameters for selected type
  const getRequiredParameters = (block) => {
    if (!block.selectedCategory) return [];
    const types = getAvailableTypes(block.selectedCategory);
    if (hasSingleType(block.selectedCategory) && types.length > 0) {
      return types[0].required_parameters || [];
    }
    if (block.selectedType) {
      return types.find((type) => type.type === block.selectedType)?.required_parameters || [];
    }
    return [];
  };

  // Check if description is required based on required_parameters
  const isDescriptionRequired = (block) => {
    if (!block.selectedCategory) return false;
    const requiredParams = getRequiredParameters(block);
    return requiredParams.some((p) => p.key === "description" && p.required);
  };

  // Handle category change
  const handleCategoryChange = (blockId, category) => {
    const block = ruleBlocks.find((b) => b.id === blockId);
    const newCategory = metadata?.categories?.find((cat) => cat.category === category);
    const newAllowedScopes = newCategory?.allowed_scopes || [];
    const allowsService = newAllowedScopes.some((scope) => scope.scope_type === "SERVICE");
    const allowsRegion = newAllowedScopes.some((scope) => scope.scope_type === "REGION");

    const updates = {
      rule_category: category,
      selectedCategory: category,
      rule_type: "",
      selectedType: null,
      selectedTypes: [],
      parameters: {},
    };

    // For non-CUSTOM categories, force frequency to RECURRING
    if (category !== "CUSTOM" && block.frequency === "ONE_TIME") {
      updates.frequency = "RECURRING";
      updates.start_date = "";
      updates.end_date = "";
    }

    if (!allowsService) {
      updates.selectedServices = [];
    }
    if (!allowsRegion) {
      updates.selectedRegions = [];
    }

    // Auto-select single type if category has only one
    const types = getAvailableTypes(category);
    if (types.length === 1) {
      updates.rule_type = types[0].type;
      updates.selectedType = types[0].type;
    }

    setRuleBlocks(
      ruleBlocks.map((block) =>
        block.id === blockId ? { ...block, ...updates } : block
      )
    );
  };

  // Handle type change
  const handleTypeChange = (blockId, type) => {
    setRuleBlocks(
      ruleBlocks.map((block) =>
        block.id === blockId
          ? {
              ...block,
              rule_type: type,
              selectedType: type,
              parameters: {},
            }
          : block
      )
    );
  };

  // Handle type toggle for boolean categories
  const handleTypeToggle = (blockId, type, checked) => {
    setRuleBlocks(
      ruleBlocks.map((block) => {
        if (block.id === blockId) {
          const selectedTypes = checked
            ? [...block.selectedTypes, type]
            : block.selectedTypes.filter((t) => t !== type);
          return {
            ...block,
            selectedTypes,
            parameters: {},
          };
        }
        return block;
      })
    );
  };

  // Handle parameter change
  const handleParameterChange = (blockId, key, value) => {
    setRuleBlocks(
      ruleBlocks.map((block) =>
        block.id === blockId
          ? {
              ...block,
              parameters: {
                ...block.parameters,
                [key]: value,
              },
            }
          : block
      )
    );
  };

  // Format date for API
  const formatDateForAPI = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toISOString().split("T")[0];
  };

  // Clear saved state
  const clearSavedState = () => {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error("Error clearing saved state:", error);
    }
  };

  // Validate and proceed to review
  const handleProceedToReview = () => {
    // Basic validation
    for (const block of ruleBlocks) {
      if (!block.rule_category) {
        setSnackbar({
          open: true,
          message: "Please select a rule category for all rules.",
          severity: "error",
        });
        return;
      }
      if (!block.name) {
        setSnackbar({
          open: true,
          message: "Please enter a billing rule name for all rules.",
          severity: "error",
        });
        return;
      }
      // Check if description is required based on required_parameters
      if (isDescriptionRequired(block) && !block.description) {
        setSnackbar({
          open: true,
          message: "Description is required for this rule type.",
          severity: "error",
        });
        return;
      }
      if (!isBooleanOnlyCategory(block.rule_category)) {
        if (!hasSingleType(block.rule_category) && !block.rule_type) {
          setSnackbar({
            open: true,
            message: "Please select a rule type for all rules.",
            severity: "error",
          });
          return;
        }
      } else {
        if (block.selectedTypes.length === 0) {
          setSnackbar({
            open: true,
            message: "Please select at least one option for all rules.",
            severity: "error",
          });
          return;
        }
      }
      if (block.frequency === "RECURRING") {
        if (!block.start_date) {
          setSnackbar({
            open: true,
            message: "Please select start month for all recurring rules.",
            severity: "error",
          });
          return;
        }
        if (!block.end_date) {
          setSnackbar({
            open: true,
            message: "Please select end month for all recurring rules.",
            severity: "error",
          });
          return;
        }
      }
    }

    // Navigate to review page with rule blocks data and metadata
    // State is already saved in sessionStorage, so we can pass it via location state
    history.push(`/billing-management/customer/${customerOrgCode}/rules/review`, {
      ruleBlocks,
      customerOrgCode,
      metadata, // Pass metadata for category and type descriptions
    });
  };

  // Handle cancel - clear saved state and navigate back
  const handleCancel = () => {
    clearSavedState();
    history.push(`/billing-management/customer/${customerOrgCode}/rules`);
  };

  // Render a single rule block
  const renderRuleBlock = (block, index) => {
    const availableTypes = getAvailableTypes(block.selectedCategory);
    const allowedScopes = getAllowedScopes(block.selectedCategory);
    const showServiceField = allowedScopes.some((scope) => scope.scope_type === "SERVICE");
    const showRegionField = allowedScopes.some((scope) => scope.scope_type === "REGION");
    const isBoolean = isBooleanOnlyCategory(block.selectedCategory);
    const hasSingle = hasSingleType(block.selectedCategory);
    const requiredParameters = getRequiredParameters(block);
    const descriptionRequired = isDescriptionRequired(block);

    return (
      <Card key={block.id} sx={{ mb: 3, border: "1px solid #e2e8f0", borderRadius: "8px" }}>
        <CardContent sx={{ p: 3 }}>
          {index === 0 && ruleBlocks.length === 1 && (
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: "#1e293b" }}>
                Rule Configuration
              </Typography>
            </Box>
          )}
          {ruleBlocks.length > 1 && (
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: "#1e293b" }}>
                Rule {index + 1}
              </Typography>
              <IconButton
                onClick={() => handleRemoveRuleBlock(block.id)}
                sx={{ color: "#ef4444" }}
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          )}

          <Box>
            {/* Form Fields */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              {/* Category Selection - First */}
              {isFetchingMetadata ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <FormControl fullWidth size="small">
                  <InputLabel>Rule Category *</InputLabel>
                  <Select
                    value={block.rule_category}
                    label="Rule Category *"
                    onChange={(e) => handleCategoryChange(block.id, e.target.value)}
                    required
                  >
                    {metadata?.categories?.map((category) => (
                      <MenuItem key={category.category} value={category.category}>
                        <Box>
                          <Typography sx={{ fontWeight: 500, fontSize: "0.875rem" }}>
                            {category.label}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              color: "#64748b",
                              mt: 0.25,
                            }}
                          >
                            {category.description}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {/* Type Selection */}
              {block.selectedCategory && availableTypes.length > 0 && isBoolean ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                  {availableTypes.map((type) => {
                    const isChecked = block.selectedTypes.includes(type.type);
                    return (
                      <Box
                        key={type.type}
                        sx={{
                          p: 1.5,
                          border: "1px solid #e2e8f0",
                          borderRadius: "8px",
                          backgroundColor: isChecked ? "#f0f9ff" : "#ffffff",
                          "&:hover": {
                            borderColor: "#cbd5e1",
                            backgroundColor: isChecked ? "#e0f2fe" : "#f8fafc",
                          },
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isChecked}
                              onChange={(e) => handleTypeToggle(block.id, type.type, e.target.checked)}
                              size="small"
                            />
                          }
                          label={
                            <Box>
                              <Typography sx={{ fontSize: "0.875rem", fontWeight: 500 }}>
                                {type.label}
                              </Typography>
                              {type.description && (
                                <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", mt: 0.5 }}>
                                  {type.description}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </Box>
                    );
                  })}
                </Box>
              ) : block.selectedCategory && hasSingle ? (
                <Box
                  sx={{
                    p: 2,
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    backgroundColor: "#f8fafc",
                  }}
                >
                  <Typography sx={{ fontSize: "0.875rem", fontWeight: 500 }}>
                    {availableTypes[0].label}
                  </Typography>
                  {availableTypes[0].description && (
                    <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", mt: 0.5 }}>
                      {availableTypes[0].description}
                    </Typography>
                  )}
                </Box>
              ) : block.selectedCategory && availableTypes.length > 0 ? (
                <FormControl fullWidth size="small">
                  <InputLabel>Rule Type *</InputLabel>
                  <Select
                    value={block.rule_type}
                    label="Rule Type *"
                    onChange={(e) => handleTypeChange(block.id, e.target.value)}
                    required
                  >
                    {availableTypes.map((type) => (
                      <MenuItem key={type.type} value={type.type}>
                        <Box>
                          <Typography sx={{ fontWeight: 500, fontSize: "0.875rem" }}>
                            {type.label}
                          </Typography>
                          <Typography sx={{ fontSize: "0.75rem", color: "#64748b", mt: 0.25 }}>
                            {type.description}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : null}

              {/* Required Parameters - Show right after Type */}
              {!isBoolean && (block.selectedType || hasSingle) && requiredParameters.length > 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {requiredParameters.map((param) => (
                    <Box key={param.key}>
                      {param.type === "boolean" ? (
                        <Box
                          sx={{
                            p: 2,
                            border: "1px solid #e2e8f0",
                            borderRadius: "8px",
                            backgroundColor: "#ffffff",
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={block.parameters[param.key] || false}
                                onChange={(e) => handleParameterChange(block.id, param.key, e.target.checked)}
                                size="small"
                              />
                            }
                            label={
                              <Box>
                                <Typography sx={{ fontSize: "0.875rem", fontWeight: 500 }}>
                                  {param.label}
                                  {param.required && (
                                    <Typography component="span" sx={{ color: "#ef4444", ml: 0.5 }}>
                                      *
                                    </Typography>
                                  )}
                                </Typography>
                                {param.description && (
                                  <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", mt: 0.5 }}>
                                    {param.description}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        </Box>
                      ) : (
                        <TextField
                          label={param.label}
                          value={block.parameters[param.key] || ""}
                          onChange={(e) => {
                            let value = e.target.value;
                            if (param.type === "number") {
                              value = value === "" ? "" : parseFloat(value) || 0;
                            }
                            handleParameterChange(block.id, param.key, value);
                          }}
                          type={param.type === "number" ? "number" : "text"}
                          required={param.required}
                          fullWidth
                          size="small"
                          multiline={param.type === "string" && param.label.toLowerCase().includes("description")}
                          rows={param.type === "string" && param.label.toLowerCase().includes("description") ? 3 : 1}
                          helperText={param.description}
                        />
                      )}
                    </Box>
                  ))}
                </Box>
              )}

              {/* Frequency - Moved after Required Parameters */}
              <FormControl component="fieldset">
                <FormLabel component="legend" sx={{ fontSize: "0.875rem", color: "#374151", mb: 1 }}>
                  Frequency
                </FormLabel>
                <RadioGroup
                  value={block.frequency}
                  onChange={(e) => updateRuleBlock(block.id, "frequency", e.target.value)}
                  row
                >
                  {metadata?.frequencies
                    ?.filter((freq) => {
                      // Only show both options for CUSTOM category
                      // For all other categories, only show RECURRING
                      if (block.selectedCategory === "CUSTOM") {
                        return true; // Show all frequencies
                      }
                      return freq.frequency === "RECURRING"; // Only show RECURRING for non-CUSTOM
                    })
                    ?.map((freq) => (
                      <FormControlLabel
                        key={freq.frequency}
                        value={freq.frequency}
                        control={<Radio size="small" />}
                        label={freq.label}
                        sx={{
                          "& .MuiFormControlLabel-label": {
                            fontSize: "0.875rem",
                          },
                        }}
                      />
                    ))}
                </RadioGroup>
              </FormControl>

              {/* Date Range - Only for RECURRING - Moved directly after Frequency */}
              {block.frequency === "RECURRING" && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {/* Start Date - Month Selector */}
                  <Box>
                    <Typography sx={{ fontSize: "0.875rem", color: "#374151", mb: 1, fontWeight: 500 }}>
                      Start Month <span style={{ color: "#ef4444" }}>*</span>
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <FormControl size="small" sx={{ flex: 1 }} required>
                        <InputLabel>Month</InputLabel>
                        <Select
                          value={block.start_date ? (parseInt(block.start_date.split("-")[1], 10) - 1) : ""}
                          label="Month"
                          onChange={(e) => {
                            const month = e.target.value;
                            const year = block.start_date ? parseInt(block.start_date.split("-")[0], 10) : new Date().getFullYear();
                            const monthStr = String(month + 1).padStart(2, "0");
                            updateRuleBlock(block.id, "start_date", `${year}-${monthStr}`);
                          }}
                        >
                          {[
                            "January", "February", "March", "April", "May", "June",
                            "July", "August", "September", "October", "November", "December"
                          ].map((month, index) => (
                            <MenuItem key={index} value={index}>
                              {month}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ flex: 1 }} required>
                        <InputLabel>Year</InputLabel>
                        <Select
                          value={block.start_date ? parseInt(block.start_date.split("-")[0], 10) : new Date().getFullYear()}
                          label="Year"
                          onChange={(e) => {
                            const year = e.target.value;
                            const month = block.start_date ? (parseInt(block.start_date.split("-")[1], 10) - 1) : new Date().getMonth();
                            const monthStr = String(month + 1).padStart(2, "0");
                            updateRuleBlock(block.id, "start_date", `${year}-${monthStr}`);
                          }}
                        >
                          {Array.from({ length: new Date().getFullYear() + 11 - 2000 }, (_, i) => {
                            const year = 2000 + i;
                            return (
                              <MenuItem key={year} value={year}>
                                {year}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>

                  {/* End Date - Month Selector */}
                  <Box>
                    <Typography sx={{ fontSize: "0.875rem", color: "#374151", mb: 1, fontWeight: 500 }}>
                      End Month <span style={{ color: "#ef4444" }}>*</span>
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <FormControl size="small" sx={{ flex: 1 }} required>
                        <InputLabel>Month</InputLabel>
                          <Select
                            value={block.end_date ? (parseInt(block.end_date.split("-")[1], 10) - 1) : ""}
                            label="Month"
                            onChange={(e) => {
                              const month = e.target.value;
                              const year = block.end_date ? parseInt(block.end_date.split("-")[0], 10) : new Date().getFullYear();
                              const monthStr = String(month + 1).padStart(2, "0");
                              updateRuleBlock(block.id, "end_date", `${year}-${monthStr}`);
                            }}
                          >
                            {[
                              "January", "February", "March", "April", "May", "June",
                              "July", "August", "September", "October", "November", "December"
                            ].map((month, index) => (
                              <MenuItem key={index} value={index}>
                                {month}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ flex: 1 }} required>
                          <InputLabel>Year</InputLabel>
                          <Select
                            value={block.end_date ? parseInt(block.end_date.split("-")[0], 10) : new Date().getFullYear()}
                            label="Year"
                            onChange={(e) => {
                              const year = e.target.value;
                              const month = block.end_date ? (parseInt(block.end_date.split("-")[1], 10) - 1) : new Date().getMonth();
                              const monthStr = String(month + 1).padStart(2, "0");
                              updateRuleBlock(block.id, "end_date", `${year}-${monthStr}`);
                            }}
                          >
                            {Array.from({ length: new Date().getFullYear() + 11 - 2000 }, (_, i) => {
                              const year = 2000 + i;
                              return (
                                <MenuItem key={year} value={year}>
                                  {year}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                      </Box>
                    </Box>
                </Box>
              )}

              {/* Rule Name */}
              <TextField
                label="Billing Rule Name"
                value={block.name}
                onChange={(e) => updateRuleBlock(block.id, "name", e.target.value)}
                required
                fullWidth
                size="small"
                helperText="The billing rule name can have up to 128 characters. Blank spaces and special characters are not valid."
              />

              {/* Description - Required based on required_parameters */}
              <TextField
                label={descriptionRequired ? "Description" : "Description - optional"}
                value={block.description}
                onChange={(e) => updateRuleBlock(block.id, "description", e.target.value)}
                required={descriptionRequired}
                fullWidth
                multiline
                rows={3}
                size="small"
                helperText="The billing rule description can have up to 1,024 characters."
              />

              {/* Linked Accounts - Multi-select with All Accounts option */}
              <Box
                sx={{
                  p: 1.5,
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  backgroundColor: "#ffffff",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                  <AccountBalanceIcon sx={{ fontSize: 18, color: "#64748b" }} />
                  <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: "#1e293b" }}>
                    Linked Accounts
                  </Typography>
                </Box>
                
                {/* All Accounts Checkbox */}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={block.allAccounts !== false}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        // Update both allAccounts and selectedAccounts in a single state update
                        setRuleBlocks(
                          ruleBlocks.map((b) =>
                            b.id === block.id
                              ? {
                                  ...b,
                                  allAccounts: isChecked,
                                  selectedAccounts: isChecked ? [] : b.selectedAccounts,
                                }
                              : b
                          )
                        );
                      }}
                      size="small"
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontSize: "0.875rem", fontWeight: 500 }}>
                        All Accounts
                      </Typography>
                      <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", mt: 0.25 }}>
                        Apply this rule to all linked accounts
                      </Typography>
                    </Box>
                  }
                  sx={{ mb: 1.5, cursor: "pointer" }}
                />

                {/* Selected Accounts Display */}
                {block.allAccounts === false && (
                  <Box sx={{ mt: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        p: 1.5,
                        border: "1px solid #e2e8f0",
                        borderRadius: "6px",
                        backgroundColor: "#f8fafc",
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        {(block.selectedAccounts || []).length > 0 ? (
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {(block.selectedAccounts || []).map((account) => (
                              <Chip
                                key={account}
                                label={account}
                                size="small"
                          onDelete={() => {
                            const currentAccounts = block.selectedAccounts || [];
                            const newAccounts = currentAccounts.filter((a) => a !== account);
                            updateRuleBlockArray(block.id, "selectedAccounts", newAccounts);
                            // If no accounts are selected, automatically set allAccounts to true
                            if (newAccounts.length === 0) {
                              updateRuleBlock(block.id, "allAccounts", true);
                            }
                          }}
                                sx={{
                                  fontSize: "0.75rem",
                                  height: 24,
                                  backgroundColor: alpha("#3b82f6", 0.1),
                                  color: "#3b82f6",
                                }}
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography sx={{ fontSize: "0.8125rem", color: "#9ca3af", fontStyle: "italic" }}>
                            No specific accounts selected
                          </Typography>
                        )}
                      </Box>
                      <FormControl size="small" sx={{ minWidth: 100 }}>
                        <Select
                          value={block.scopeOperatorAccounts || "INCLUDE"}
                          onChange={(e) => updateRuleBlock(block.id, "scopeOperatorAccounts", e.target.value)}
                          displayEmpty
                          sx={{ fontSize: "0.8125rem", height: 36 }}
                        >
                          <MenuItem value="INCLUDE">Include</MenuItem>
                          <MenuItem value="EXCLUDE">Exclude</MenuItem>
                        </Select>
                      </FormControl>
                      <Button
                        variant="outlined"
                        endIcon={<ArrowDropDownIcon />}
                        onClick={(e) => {
                          setPopoverStates({
                            ...popoverStates,
                            [`account-${block.id}`]: {
                              open: true,
                              anchorEl: e.currentTarget,
                              searchTerm: "",
                            },
                          });
                        }}
                        disabled={block.allAccounts === true}
                        sx={{
                          textTransform: "none",
                          fontSize: "0.8125rem",
                          borderColor: "#e2e8f0",
                          color: "#475569",
                          minWidth: 80,
                        }}
                      >
                        Add
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Service and Region Filters - Moved from right side, after Linked Accounts */}
              {(showServiceField || showRegionField) && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#1e293b", mb: 1 }}>
                    Filters
                  </Typography>

                  {/* Service Field */}
                  {showServiceField && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        p: 1.5,
                        border: "1px solid #e2e8f0",
                        borderRadius: "8px",
                        backgroundColor: "#ffffff",
                      }}
                    >
                      <CloudIcon sx={{ fontSize: 18, color: "#64748b" }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: "#1e293b", mb: 0.5 }}>
                          Service
                        </Typography>
                        {block.selectedServices.length > 0 ? (
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                            {block.selectedServices.map((service) => (
                              <Chip
                                key={service}
                                label={service}
                                size="small"
                                onDelete={() => {
                                  updateRuleBlockArray(block.id, "selectedServices", block.selectedServices.filter((s) => s !== service));
                                }}
                                sx={{
                                  fontSize: "0.75rem",
                                  height: 24,
                                  backgroundColor: alpha("#3b82f6", 0.1),
                                  color: "#3b82f6",
                                }}
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography sx={{ fontSize: "0.8125rem", color: "#9ca3af", fontStyle: "italic" }}>
                            No services selected
                          </Typography>
                        )}
                      </Box>
                      <FormControl size="small" sx={{ minWidth: 100 }}>
                        <Select
                          value={block.scopeOperatorServices || "INCLUDE"}
                          onChange={(e) => updateRuleBlock(block.id, "scopeOperatorServices", e.target.value)}
                          displayEmpty
                          sx={{ fontSize: "0.8125rem", height: 36 }}
                        >
                          <MenuItem value="INCLUDE">Include</MenuItem>
                          <MenuItem value="EXCLUDE">Exclude</MenuItem>
                        </Select>
                      </FormControl>
                      <Button
                        variant="outlined"
                        endIcon={<ArrowDropDownIcon />}
                        onClick={(e) => {
                          setPopoverStates({
                            ...popoverStates,
                            [`service-${block.id}`]: {
                              open: true,
                              anchorEl: e.currentTarget,
                              searchTerm: "",
                            },
                          });
                        }}
                        disabled={isFetchingServiceCodes}
                        sx={{
                          textTransform: "none",
                          fontSize: "0.8125rem",
                          borderColor: "#e2e8f0",
                          color: "#475569",
                          minWidth: 80,
                        }}
                      >
                        Add
                      </Button>
                    </Box>
                  )}

                  {/* Region Field */}
                  {showRegionField && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        p: 1.5,
                        border: "1px solid #e2e8f0",
                        borderRadius: "8px",
                        backgroundColor: "#ffffff",
                      }}
                    >
                      <PublicIcon sx={{ fontSize: 18, color: "#64748b" }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: "#1e293b", mb: 0.5 }}>
                          Region
                        </Typography>
                        {block.selectedRegions.length > 0 ? (
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                            {block.selectedRegions.map((region) => (
                              <Chip
                                key={region}
                                label={region}
                                size="small"
                                onDelete={() => {
                                  updateRuleBlockArray(block.id, "selectedRegions", block.selectedRegions.filter((r) => r !== region));
                                }}
                                sx={{
                                  fontSize: "0.75rem",
                                  height: 24,
                                  backgroundColor: alpha("#3b82f6", 0.1),
                                  color: "#3b82f6",
                                }}
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography sx={{ fontSize: "0.8125rem", color: "#9ca3af", fontStyle: "italic" }}>
                            No regions selected
                          </Typography>
                        )}
                      </Box>
                      <FormControl size="small" sx={{ minWidth: 100 }}>
                        <Select
                          value={block.scopeOperatorRegions || "INCLUDE"}
                          onChange={(e) => updateRuleBlock(block.id, "scopeOperatorRegions", e.target.value)}
                          displayEmpty
                          sx={{ fontSize: "0.8125rem", height: 36 }}
                        >
                          <MenuItem value="INCLUDE">Include</MenuItem>
                          <MenuItem value="EXCLUDE">Exclude</MenuItem>
                        </Select>
                      </FormControl>
                      <Button
                        variant="outlined"
                        endIcon={<ArrowDropDownIcon />}
                        onClick={(e) => {
                          setPopoverStates({
                            ...popoverStates,
                            [`region-${block.id}`]: {
                              open: true,
                              anchorEl: e.currentTarget,
                              searchTerm: "",
                            },
                          });
                        }}
                        disabled={isFetchingRegions}
                        sx={{
                          textTransform: "none",
                          fontSize: "0.8125rem",
                          borderColor: "#e2e8f0",
                          color: "#475569",
                          minWidth: 80,
                        }}
                      >
                        Add
                      </Button>
                    </Box>
                  )}
                </Box>
              )}
            </Box>

          </Box>
        </CardContent>
      </Card>
    );
  };


  return (
    <Box sx={{ p: 3, backgroundColor: "#f5f5f5", minHeight: "100vh", pb: 8, mt: "4rem", mb: "3rem" }}>
      {/* Breadcrumbs with Back Button */}
      <Box sx={{ mb: 2.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Breadcrumbs
          separator={
            <Box
              component="span"
              sx={{
                color: "#cbd5e1",
                fontSize: "0.875rem",
                mx: 0.5,
                display: "flex",
                alignItems: "center",
              }}
            >
              /
            </Box>
          }
          aria-label="breadcrumb"
          sx={{
            "& .MuiBreadcrumbs-ol": {
              flexWrap: "nowrap",
            },
          }}
        >
          <Link
            component="button"
            variant="body2"
            onClick={() => history.push("/billing-management")}
            sx={{
              color: "#64748b",
              textDecoration: "none",
              fontSize: "0.875rem",
              fontWeight: 400,
              transition: "color 0.2s ease",
              "&:hover": {
                color: "#1e293b",
              },
              cursor: "pointer",
              px: 0.5,
            }}
          >
            Billing Management
          </Link>
          <Link
            component="button"
            variant="body2"
            onClick={() => history.push(`/billing-management/customer/${customerOrgCode}/rules`)}
            sx={{
              color: "#64748b",
              textDecoration: "none",
              fontSize: "0.875rem",
              fontWeight: 400,
              transition: "color 0.2s ease",
              "&:hover": {
                color: "#1e293b",
              },
              cursor: "pointer",
              px: 0.5,
            }}
          >
            Billing Rules
          </Link>
          <Typography
            variant="body2"
            sx={{
              color: "#3b82f6",
              fontSize: "0.875rem",
              fontWeight: 600,
              px: 0.5,
            }}
          >
            Create Billing {ruleBlocks.length > 1 ? "Rules" : "Rule"}
          </Typography>
        </Breadcrumbs>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => {
            clearSavedState();
            history.push(`/billing-management/customer/${customerOrgCode}/rules`);
          }}
          sx={{
            textTransform: "none",
            color: "#64748b",
            fontSize: "0.875rem",
            fontWeight: 500,
            "&:hover": {
              backgroundColor: alpha("#3b82f6", 0.1),
              color: "#3b82f6",
            },
          }}
        >
          Back
        </Button>
      </Box>

      {/* Rule Blocks */}
      {ruleBlocks.map((block, index) => renderRuleBlock(block, index))}

      {/* Add Rule Button and Action Buttons */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
        <Button
          startIcon={<AddIcon />}
          onClick={handleAddRuleBlock}
          variant="outlined"
          sx={{
            textTransform: "none",
            borderColor: "#3b82f6",
            color: "#3b82f6",
            "&:hover": {
              borderColor: "#2563eb",
              backgroundColor: alpha("#3b82f6", 0.05),
            },
          }}
        >
          Add billing rule
        </Button>

        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            onClick={handleCancel}
            sx={{
              textTransform: "none",
              color: "#475569",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleProceedToReview}
            variant="contained"
            sx={{
              textTransform: "none",
              backgroundColor: "#3b82f6",
              "&:hover": {
                backgroundColor: "#2563eb",
              },
            }}
          >
            Review billing rules
          </Button>
        </Box>
      </Box>

      {/* Service Popover */}
      {Object.keys(popoverStates).map((key) => {
        if (!key.startsWith("service-")) return null;
        const blockId = parseInt(key.split("-")[1]);
        const state = popoverStates[key];
        const block = ruleBlocks.find((b) => b.id === blockId);
        if (!block || !state) return null;

        return (
          <Popover
            key={key}
            open={state.open}
            anchorEl={state.anchorEl}
            onClose={() => {
              setPopoverStates({
                ...popoverStates,
                [key]: { ...state, open: false },
              });
            }}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Box sx={{ p: 1.5, minWidth: 300, maxHeight: 400 }}>
              <TextField
                size="small"
                placeholder="Search services..."
                value={state.searchTerm || ""}
                onChange={(e) => {
                  setPopoverStates({
                    ...popoverStates,
                    [key]: { ...state, searchTerm: e.target.value },
                  });
                }}
                fullWidth
                sx={{ mb: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ maxHeight: 300, overflow: "auto" }}>
                {serviceCodes
                  .filter((service) =>
                    state.searchTerm
                      ? service.toLowerCase().includes(state.searchTerm.toLowerCase())
                      : true
                  )
                  .map((service) => (
                    <MenuItem
                      key={service}
                      onClick={() => {
                        if (!block.selectedServices.includes(service)) {
                          updateRuleBlock(block.id, "selectedServices", [...block.selectedServices, service]);
                        }
                      }}
                      disabled={block.selectedServices.includes(service)}
                      sx={{
                        fontSize: "0.875rem",
                        py: 0.75,
                        backgroundColor: block.selectedServices.includes(service) ? alpha("#3b82f6", 0.1) : "transparent",
                      }}
                    >
                      {service}
                    </MenuItem>
                  ))}
              </Box>
            </Box>
          </Popover>
        );
      })}

      {/* Region Popover */}
      {Object.keys(popoverStates).map((key) => {
        if (!key.startsWith("region-")) return null;
        const blockId = parseInt(key.split("-")[1]);
        const state = popoverStates[key];
        const block = ruleBlocks.find((b) => b.id === blockId);
        if (!block || !state) return null;

        return (
          <Popover
            key={key}
            open={state.open}
            anchorEl={state.anchorEl}
            onClose={() => {
              setPopoverStates({
                ...popoverStates,
                [key]: { ...state, open: false },
              });
            }}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Box sx={{ p: 1.5, minWidth: 300, maxHeight: 400 }}>
              <TextField
                size="small"
                placeholder="Search regions..."
                value={state.searchTerm || ""}
                onChange={(e) => {
                  setPopoverStates({
                    ...popoverStates,
                    [key]: { ...state, searchTerm: e.target.value },
                  });
                }}
                fullWidth
                sx={{ mb: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ maxHeight: 300, overflow: "auto" }}>
                {regions
                  .filter((region) =>
                    state.searchTerm
                      ? region.toLowerCase().includes(state.searchTerm.toLowerCase())
                      : true
                  )
                  .map((region) => (
                    <MenuItem
                      key={region}
                      onClick={() => {
                        if (!block.selectedRegions.includes(region)) {
                          updateRuleBlockArray(block.id, "selectedRegions", [...block.selectedRegions, region]);
                        }
                      }}
                      disabled={block.selectedRegions.includes(region)}
                      sx={{
                        fontSize: "0.875rem",
                        py: 0.75,
                        backgroundColor: block.selectedRegions.includes(region) ? alpha("#3b82f6", 0.1) : "transparent",
                      }}
                    >
                      {region}
                    </MenuItem>
                  ))}
              </Box>
            </Box>
          </Popover>
        );
      })}

      {/* Account Popover */}
      {Object.keys(popoverStates).map((key) => {
        if (!key.startsWith("account-")) return null;
        const blockId = parseInt(key.split("-")[1]);
        const state = popoverStates[key];
        const block = ruleBlocks.find((b) => b.id === blockId);
        if (!block || !state) return null;

        return (
          <Popover
            key={key}
            open={state.open}
            anchorEl={state.anchorEl}
            onClose={() => {
              setPopoverStates({
                ...popoverStates,
                [key]: { ...state, open: false },
              });
            }}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Box sx={{ p: 1.5, minWidth: 300, maxHeight: 400 }}>
              <TextField
                size="small"
                placeholder="Search accounts..."
                value={state.searchTerm || ""}
                onChange={(e) => {
                  setPopoverStates({
                    ...popoverStates,
                    [key]: { ...state, searchTerm: e.target.value },
                  });
                }}
                fullWidth
                sx={{ mb: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ maxHeight: 300, overflow: "auto" }}>
                {metadata?.accounts
                  ?.filter((account) =>
                    state.searchTerm
                      ? account.toLowerCase().includes(state.searchTerm.toLowerCase())
                      : true
                  )
                  .map((account) => (
                    <MenuItem
                      key={account}
                      onClick={() => {
                        const currentAccounts = block.selectedAccounts || [];
                        if (!currentAccounts.includes(account)) {
                          // If selecting a specific account, uncheck "All Accounts"
                          updateRuleBlock(block.id, "allAccounts", false);
                          updateRuleBlockArray(block.id, "selectedAccounts", [...currentAccounts, account]);
                        }
                      }}
                      disabled={(block.selectedAccounts || []).includes(account) || block.allAccounts === true}
                      sx={{
                        fontSize: "0.875rem",
                        py: 0.75,
                        backgroundColor: (block.selectedAccounts || []).includes(account) ? alpha("#3b82f6", 0.1) : "transparent",
                      }}
                    >
                      {account}
                    </MenuItem>
                  ))}
              </Box>
            </Box>
          </Popover>
        );
      })}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CreateBillingRule;
